/**
 * The signed-in user's apps, as the BFF decides them (#1610).
 *
 * `GET /apps/me` applies the one access rule (system apps, role grants the
 * organisation may see, everything for admins). Start menu, App Store,
 * window opening and deep links all read this list instead of assembling
 * their own. Cached for a minute and shared between concurrent callers, so
 * opening a window does not cost a request; a grant changed in Cortex shows
 * up after at most that long or on `invalidate()`.
 */

import { apiRequest } from '@/lib/api-client'
import { logError } from '@/lib/logger'
import type { RegisteredApp } from './backend-apps-service'

export type MyAppKind = 'system' | 'admin' | 'assignable' | 'url'

export interface MyApp extends RegisteredApp {
  kind: MyAppKind
}

interface MyAppListResponse {
  apps: MyApp[]
  total: number
}

const TTL_MS = 60_000

let cache: { apps: MyApp[]; loadedAt: number } | null = null
let inFlight: Promise<MyApp[]> | null = null

async function load(): Promise<MyApp[]> {
  try {
    const response = await apiRequest<MyAppListResponse>('/apps/me')
    const apps = response?.apps ?? []
    cache = { apps, loadedAt: Date.now() }
    return apps
  } catch (error) {
    logError(error as Error, { context: 'MyAppsService.load' })
    return []
  } finally {
    inFlight = null
  }
}

export const myAppsService = {
  /** The caller's apps; an unreadable list counts as no apps. */
  async getMyApps(): Promise<MyApp[]> {
    if (cache && Date.now() - cache.loadedAt < TTL_MS) return cache.apps
    inFlight ??= load()
    return inFlight
  },

  /** Whether the caller has the app with this backend name. */
  async hasApp(appName: string): Promise<boolean> {
    const apps = await this.getMyApps()
    return apps.some((app) => app.name === appName)
  },

  invalidate(): void {
    cache = null
  },
}
